import {
  Alert,
  Label,
  TextInput,
} from 'flowbite-react';
import { useEffect, useRef, useState } from 'react';
import AuthApi from '../../Api/AuthApi';
import { jwtDecode } from 'jwt-decode';
import { DecodedToken } from '../../Models/DecodedToken';
import { useAppDispatch } from '../../Reducers/store';
import { fetchCurrentUser, setUserFromToken } from '../../Actions';
import { toast } from 'react-toastify';
import Loader from '../Common/Loader';
import emdaLogo from '../../Assets/EMDA150.png';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import React from 'react';

export default function Login() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const returnPath = (location.state as { from?: string })?.from;
  const [showError, setShowError] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [mode, setMode] = useState<'password' | 'code'>('password');
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const codeInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (mode === 'code' && step === 'code') {
      // Focus the code input when the code screen shows (mobile-friendly)
      // Delay to ensure the input is rendered and ready
      const timeoutId = setTimeout(() => {
        codeInputRef.current?.focus();
      }, 200);
      return () => clearTimeout(timeoutId);
    }
  }, [mode, step]);

  const handleAuthSuccess = (token: string) => {
    localStorage.setItem('EMDAToken', token);
    const decoded: DecodedToken = jwtDecode(token);
    dispatch(
      setUserFromToken({
        email:
          decoded[
            'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'
          ],
        exp: decoded.exp,
      })
    );
    dispatch(fetchCurrentUser());
    toast.success('Success!');
    setIsLoading(false);
    navigate(returnPath || '/', { replace: true });
  };

  const requestCode = async () => {
    try {
      setShowError(false);
      setIsLoading(true);
      await AuthApi.requestLoginCode({ email: email.trim() });
      toast.success('Login code sent (check your email).');
      setStep('code');
      setCode(''); // Clear any previous code
      // Focus the code input after state updates
      setTimeout(() => {
        codeInputRef.current?.focus();
      }, 250);
    } catch {
      // For privacy, keep message generic.
      toast.success('Login code sent (check your email).');
      setStep('code');
      setCode(''); // Clear any previous code
      // Focus the code input after state updates
      setTimeout(() => {
        codeInputRef.current?.focus();
      }, 250);
    } finally {
      setIsLoading(false);
    }
  };

  const verifyCode = async (e: React.ChangeEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      setShowError(false);
      setIsLoading(true);
      return AuthApi.verifyLoginCode({ email: email.trim(), code: code.trim() })
        .then((res) => {
          handleAuthSuccess(res.data);
        })
        .catch(() => {
          setShowError(true);
          setIsLoading(false);
        });
    } catch (error) {
      setIsLoading(false);
      console.error(error);
    }
  };

  const loginWithPassword = async (e: React.ChangeEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      setShowError(false);
      setIsLoading(true);
      return AuthApi.login({ email: email.trim(), password })
        .then((res) => {
          handleAuthSuccess(res.data);
        })
        .catch(() => {
          setShowError(true);
          setIsLoading(false);
        });
    } catch (error) {
      setIsLoading(false);
      console.error(error);
    }
  };

  const handleSubmit = (e: React.ChangeEvent<HTMLFormElement>) => {
    if (mode === 'password') {
      return loginWithPassword(e);
    }
    return verifyCode(e);
  };

  return (
    <>
      <div className='w-full p-4'>
        {isLoading && <Loader />}
        {/* <Loader></Loader> */}
      </div>
      <div className='flex flex-col items-center justify-center px-6 py-8 mx-auto lg:py-0'>
        <a
          href='/'
          className='flex items-center mb-6 text-2xl font-semibold text-gray-900 dark:text-white'
        >
          <img
            className='h-20 mr-2'
            src={emdaLogo}
            width='100'
            alt='logo'
          ></img>{' '}
          Member Portal
        </a>
        <div className='w-full bg-white rounded-lg shadow dark:border md:mt-0 sm:max-w-md xl:p-0 dark:bg-gray-800 dark:border-gray-700'>
          <div className='px-6 pt-6 space-y-4 md:space-y-6'>
            <div className='flex flex-col items-center justify-center'>
              <h1 className='text-xl font-bold text-gray-900 md:text-2xl dark:text-white'>
                Sign in to your account
              </h1>
            </div>
            <div className='flex gap-2 justify-center'>
              <button
                type='button'
                onClick={() => {
                  setMode('password');
                  setStep('email');
                }}
                className={`text-sm px-3 py-1 rounded ${
                  mode === 'password'
                    ? 'bg-mainButton text-white'
                    : 'bg-gray-200'
                }`}
              >
                Username & Password
              </button>
              <button
                type='button'
                onClick={() => {
                  setMode('code');
                  setStep('email');
                }}
                className={`text-sm px-3 py-1 rounded ${
                  mode === 'code' ? 'bg-mainButton text-white' : 'bg-gray-200'
                }`}
              >
                Email Code
              </button>
            </div>
            <form className='flex max-w-md flex-col gap-4' onSubmit={handleSubmit}>
              <div>
                <div className='mb-2 block'>
                  <Label htmlFor='email' value='Email or username' />
                </div>
                <TextInput
                  id='email'
                  type='email'
                  placeholder='you@email.com'
                  onChange={(e) => setEmail(e.target.value)}
                  value={email}
                  required
                />
              </div>
              {mode === 'password' && (
                <div>
                  <div className='mb-2 block'>
                    <Label htmlFor='password' value='Password' />
                  </div>
                  <TextInput
                    id='password'
                    type='password'
                    placeholder='••••••••'
                    onChange={(e) => setPassword(e.target.value)}
                    value={password}
                    required
                  />
                </div>
              )}
              {mode === 'code' && step === 'code' && (
                <div>
                  <div className='mb-2 block'>
                    <Label htmlFor='code' value='Login code' />
                  </div>
                  <TextInput
                    id='code'
                    type='text'
                    inputMode='numeric'
                    placeholder='123456'
                    onChange={(e) => setCode(e.target.value)}
                    value={code}
                    required
                    ref={codeInputRef}
                  />
                </div>
              )}
              {showError && (
                <Alert color='failure'>Authorization Failed!</Alert>
              )}
              {mode === 'password' ? (
                <button
                  type='submit'
                  className='focus:outline-none text-white bg-mainButton hover:bg-green-800 focus:ring-4 focus:ring-green-300 font-medium rounded-lg text-sm px-5 py-2.5 mb-2'
                >
                  Sign in
                </button>
              ) : step === 'email' ? (
                <button
                  type='button'
                  onClick={requestCode}
                  className='focus:outline-none text-white bg-mainButton hover:bg-green-800 focus:ring-4 focus:ring-green-300 font-medium rounded-lg text-sm px-5 py-2.5 mb-2'
                >
                  Send login code
                </button>
              ) : (
                <>
                  <button
                    type='submit'
                    className='focus:outline-none text-white bg-mainButton hover:bg-green-800 focus:ring-4 focus:ring-green-300 font-medium rounded-lg text-sm px-5 py-2.5 mb-2'
                  >
                    Sign in
                  </button>
                  <button
                    type='button'
                    onClick={requestCode}
                    className='text-sm underline text-mainButton'
                  >
                    Resend code
                  </button>
                </>
              )}
            </form>
          </div>
          <div className='text-right p-2 text-sm'>
            <Link to='/' className='hover:underline' style={{ color: 'blue' }}>
              Back to site
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
